import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitMessage('Thank you for subscribing!');
      setEmail('');
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 3000);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="relative bg-[#1C1C1C] mt-20">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[20rem] font-bold text-[#2A2A2A] opacity-50 whitespace-nowrap">
          ACE
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/5395687eba2a734bcb2dd68c996c233a/5a5afc594e796b563571738e53b59599.jpeg" 
                alt="ACE Consultancy Africa" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <div className="text-3xl font-bold text-white">ACE</div>
                <div className="text-base text-white/80">Consultancy Africa</div>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              Empowering African Excellence through strategic consulting and comprehensive business solutions.
            </p>
            <button className="border-2 border-[#D4AF37] text-[#D4AF37] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#D4AF37] hover:text-white transition-all whitespace-nowrap">
              Connect With Us
            </button>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white mb-6">Our Services</h4>
            <button onClick={() => scrollToSection('services')} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
              Home Security Consulting
            </button>
            <button onClick={() => scrollToSection('services')} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
              Market Research
            </button>
            <button onClick={() => scrollToSection('services')} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
              Asset Sales Platform
            </button>
            <button onClick={() => scrollToSection('services')} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
              Grant Writing
            </button>
            <button onClick={() => scrollToSection('services')} className="block text-sm text-gray-400 hover:text-[#D4AF37] transition-colors text-left">
              Recruitment Services
            </button>
          </div>

          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white mb-6">Connect</h4>
            <a href="https://web.facebook.com/aceconsultancyafrica" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
              <i className="ri-facebook-fill text-xl"></i>
              <span>Facebook</span>
            </a>
            <a href="https://www.linkedin.com/in/orku-robertson-88147814a/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
              <i className="ri-linkedin-fill text-xl"></i>
              <span>LinkedIn</span>
            </a>
            <a href="#" className="flex items-center gap-3 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
              <i className="ri-twitter-x-line text-xl"></i>
              <span>Twitter</span>
            </a>
            <div className="pt-4 space-y-2">
              <a href="mailto:info@aceconsultancy.africa" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
                <i className="ri-mail-line"></i>
                <span>info@aceconsultancy.africa</span>
              </a>
              <a href="tel:+233573005300" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
                <i className="ri-phone-line"></i>
                <span>+233 573 005 300</span>
              </a>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-white mb-4">
              Stay updated with<br />industry insights
            </h4>
            <form onSubmit={handleSubmit} className="relative">
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address" 
                className="w-full bg-[#2A2A2A] border border-gray-700 text-white px-6 py-3 pr-14 rounded-full text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                disabled={isSubmitting}
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="absolute right-1 top-1 w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-[#D4AF37] transition-all disabled:opacity-50"
              >
                <i className="ri-arrow-right-line text-gray-900 text-lg"></i>
              </button>
            </form>
            {submitMessage && (
              <p className={`text-sm ${submitMessage.includes('Thank') ? 'text-green-400' : 'text-red-400'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div>
              © 2025 ACE Consultancy Africa. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-[#D4AF37] transition-colors">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
